/**
 * Translates a DataGrid's state into the page/sort/filter request the server-mode grid
 * endpoints expect, so the database returns one page instead of the whole table.
 */

/**
 * @typedef {Object} GridQueryRequest
 * @property {number} page Zero-based page index.
 * @property {number} pageSize
 * @property {string|null} sortField
 * @property {string|null} sortDirection
 * @property {string} filterLogic 'and' | 'or'
 * @property {Array<{field: string, operator: string, value: string|null, values: string[]|null}>} filters
 */

/**
 * @param {Object} state
 * @param {number} state.page
 * @param {number} state.pageSize
 * @param {Array<{field: string, sort: string}>} [state.sortModel]
 * @param {{items?: Array<Object>, linkOperator?: string}} [state.filterModel]
 * @return {GridQueryRequest}
 */
export function toGridQuery ({ page, pageSize, sortModel, filterModel }) {
    const sort = Array.isArray(sortModel) && sortModel.length > 0 ? sortModel[0] : null;

    return {
        page,
        pageSize,
        sortField: sort?.field ?? null,
        sortDirection: sort?.sort ?? null,
        filterLogic: filterModel?.linkOperator ?? 'and',
        filters: (filterModel?.items ?? [])
            // The grid keeps an item in the model while the user is still choosing a column or
            // operator; sending those would filter on nothing.
            .filter((item) => item.columnField && item.operatorValue)
            .map((item) => ({
                field: item.columnField,
                operator: item.operatorValue,
                value: Array.isArray(item.value) ? null : (item.value ?? null),
                values: Array.isArray(item.value) ? item.value.map((v) => String(v)) : null,
            })),
    };
}

/**
 * Saves a CSV payload returned by an export endpoint to the user's machine.
 * @param {Blob} blob
 * @param {string} filename
 */
export function downloadCsv (blob, filename) {
    const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}